// Process — the ONE confirm text and the ONE call, shared by the orders list's "Process orders"
// and the order page's "Process order" button (Fernando, 2026-09-24: the desk should not have
// to go back to the list, find the order, tick it and process it). Two copies of this text
// would drift; the split-shipment warnings in particular must say the same thing everywhere.
import { authFetch, API } from './api';
import { showProcessDialog, HeldRound } from './ProcessDialog';
import { packingText } from './packingText';
import { trackLang } from './pp/track';

export interface ProcessSplit {
  active_parcel_no: number | null;
  waiting: number;
  between: boolean;
}
export interface ProcessItem {
  id: string;
  order_number?: string | null;
  split?: ProcessSplit | null;
  /** the order has a live label that has never been sent to a printer */
  labelUnprinted?: boolean;
}

export function processConfirmText(items: ProcessItem[]): string {
  const count = items.length;
  // Split shipments: Process on an order with a later parcel dispatches the CURRENT
  // parcel only (WMS-only, Partly shipped); the shop is told when the last one goes.
  const partial = items.filter(o => o.split && o.split.active_parcel_no && o.split.waiting > 0);
  const between = items.filter(o => o.split && o.split.between);
  const unprinted = items.filter(o => o.labelUnprinted);
  const splitNote = (partial.length
    ? `\n\n${partial.length} of them ${partial.length === 1 ? 'is a split shipment' : 'are split shipments'}: this dispatches the current parcel only and parks the order in Partly shipped — the shop is told when the last parcel has gone.`
    : '') + (between.length
    ? `\n\n${between.length === 1 ? 'Order' : 'Orders'} ${between.map(o => o.order_number || o.id).join(', ')}: a parcel is waiting to be released — Process will REFUSE ${between.length === 1 ? 'it' : 'them'} (release or cancel the waiting parcel on the order first). Orders before it in the batch are still processed.`
    : '') + (unprinted.length
    ? `\n\nThe label of ${unprinted.map(o => o.order_number || o.id).join(', ')} has not been printed yet — print it first?`
    : '');
  return `Process ${count} order${count > 1 ? 's' : ''}?\n\nThis will mark them as processed in the store (fulfilled + tracking sent to the customer) and move them to Archived. This cannot be undone.${splitNote}`;
}

// the language of the person at the desk: the one chosen on a tablet, else the login's own preference
let _myLang: string | null = null;
async function deskLang(): Promise<string> {
  if (trackLang()) return trackLang();
  if (_myLang === null) {
    try { const r = await authFetch(`${API}/prefs`); _myLang = r.ok ? ((await r.json())?.lang || 'en') : 'en'; }
    catch { _myLang = 'en'; }
  }
  return _myLang || 'en';
}

export interface ProcessResult {
  processed: number;
  ids: string[];
  label_issue?: any[];
  not_packed?: any[];
  /** P&P rounds whose last order this Process closed — the recap popup (2026-09-28) */
  rounds_closed?: number[];
  /** orders held because their P&P round is not fully packed (2026-10-05) */
  round_unpacked?: HeldRound[];
}

/** Ask, then process. Returns the server's answer when it accepted the call (the caller
 * refreshes and shows the round recap when a round closed), null when nothing was sent. */
export async function confirmAndProcess(items: ProcessItem[]): Promise<ProcessResult | null> {
  if (!items.length) return null;
  const tx = packingText(await deskLang());
  const count = items.length;
  // the empty-boxes check before every Process (Fernando, 2026-10-05)
  const go = await showProcessDialog({ kind: 'confirm', tx, title: `Process ${count} order${count > 1 ? 's' : ''}?`,
                                       text: processConfirmText(items).split('\n\n').slice(1).join('\n\n') });
  if (!go.ok) return null;
  const post = async (body: Record<string, unknown>) => {
    const res = await authFetch(`${API}/orders/process`, { method: 'POST', body: JSON.stringify(body) });
    return { res, data: await res.json().catch(() => ({})) };
  };
  let { res, data } = await post({ ids: items.map(i => i.id) });
  if (!res.ok) { alert(`Processing stopped.\n\n${typeof data.detail === 'string' ? data.detail : (data.detail?.message || res.status)}`); return null; }
  // a P&P round not fully packed: its orders were held, nothing of them processed (2026-10-05)
  const held: HeldRound[] = data.round_unpacked || [];
  if (held.length) {
    const canForce = ['admin', 'manager'].includes(localStorage.getItem('wms_role') || '');
    const a = await showProcessDialog({ kind: 'held', tx, title: tx.roundHeldTitle, rounds: held, processed: data.processed || 0, canForce });
    if (a.ok && a.force) {
      // the server's W-ids of the held orders — an order ticked on another page is only {id} here, so a
      // match on the order number left it out (review 5.10)
      const heldIds = new Set(held.flatMap(h => h.order_ids || []));
      const again = await post({ ids: items.filter(i => heldIds.has(String(i.id))).map(i => i.id),
                                 force: true, note: a.note });
      if (!again.res.ok) { alert(`Processing stopped.\n\n${typeof again.data.detail === 'string' ? again.data.detail : (again.data.detail?.message || again.res.status)}`); }
      else {
        data = { ...again.data, processed: (data.processed || 0) + (again.data.processed || 0),
                 ids: [...(data.ids || []), ...(again.data.ids || [])],
                 rounds_closed: [...(data.rounds_closed || []), ...(again.data.rounds_closed || [])],
                 label_issue: [...(data.label_issue || []), ...(again.data.label_issue || [])],
                 not_packed: [...(data.not_packed || []), ...(again.data.not_packed || [])] };
      }
    }
  }
  // The two rules (2026-09-28): an order without a label went to Label issue, an order not
  // fully packed stayed where it is — say which, so nobody thinks they were processed.
  const li: any[] = data.label_issue || [], np: any[] = data.not_packed || [];
  if (li.length || np.length) {
    const lines = [`${data.processed ?? 0} order${data.processed === 1 ? '' : 's'} processed.`];
    if (li.length) lines.push(`\nMoved to Label issue (no shipping label): ${li.map(o => o.order_number || o.id).join(', ')}`);
    if (np.length) lines.push(`\nNOT processed — not every piece was packed in a packing session:\n`
      + np.map(o => `• ${o.order_number || o.id}: ${(o.not_packed || []).map((l: any) => `${l.name} ${l.packed}/${l.ordered}`).join(', ')}`).join('\n')
      + `\nPack ${np.length === 1 ? 'it' : 'them'} in a packing session (scan every piece), then Process.`);
    alert(lines.join('\n'));
  }
  return data as ProcessResult;
}
